import { Ml_Log_Status, PlainMlLogDto } from "@generated/graphql";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AccordionIcon from "assets/icons/AccordionIcon";
import { useEffect, useState } from "react";
import { getQuestionsGenLogsStatus } from "helpers/index";
import QuestionsGeneratingLogStatus from "components/QuestionsGeneratingLogStatus";
import dayjs from "dayjs";
import { RefetchFunction } from "@apollo/client/react/hooks/useSuspenseQuery";

export type QuestionsGenLog = Pick<PlainMlLogDto, "createdAt" | "id" | "status"> & { skillTitle: string };

interface QuestionsGeneratingLogsProps {
  logs: QuestionsGenLog[] | undefined;
  refetchLogs: RefetchFunction<unknown, Record<string, never>>;
  afterRefetchLogs?: RefetchFunction<unknown, Record<string, never>>;
}

const QuestionsGeneratingLogs = ({ logs, refetchLogs, afterRefetchLogs }: QuestionsGeneratingLogsProps) => {
  const [expanded, setExpanded] = useState(false);
  const [refetchTimeout, setRefetchTimeout] = useState<NodeJS.Timeout | null>(null);
  const hide = !logs?.length;
  const logsStatus = getQuestionsGenLogsStatus(logs);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (logsStatus === Ml_Log_Status.Initialized && !refetchTimeout) {
      const timeout = setTimeout(
        () =>
          refetchLogs()
            .then(() => {
              if (afterRefetchLogs) afterRefetchLogs();
            })
            .finally(() => {
              setRefetchTimeout(null);
            }),
        30000
      );

      setRefetchTimeout(timeout);
    }
  }, [logs, refetchTimeout]);

  useEffect(() => {
    if (hide) setExpanded(false);
  }, [hide]);

  return (
    <Accordion
      expanded={expanded}
      onChange={toggleExpanded}
      sx={{
        ...(hide && {
          visibility: "hidden",
          opacity: 0,
        }),
        maxHeight: "500px",
        overflow: "auto",
        borderRadius: 2,
        "&:first-of-type": { borderRadius: 2 },
        "&.Mui-expanded": { marginBottom: 0 },
        "& .MuiAccordionSummary-root": { minHeight: "auto" },
        "& .MuiAccordionSummary-content": { alignItems: "center", marginY: 0 },
        "& .MuiAccordionSummary-content.Mui-expanded": { marginY: 0 },
      }}>
      <AccordionSummary>
        <Typography>Генерация вопросов</Typography>
        {logsStatus && (
          <IconButton
            sx={{
              width: 36,
              height: 36,
            }}>
            <QuestionsGeneratingLogStatus status={logsStatus} />
          </IconButton>
        )}
        <AccordionActions sx={{ marginLeft: "auto", padding: 0 }}>
          <IconButton
            sx={{
              width: 36,
              height: 36,
              color: "base.200",
              transition: "transform 300ms ease",
              transform: expanded ? "rotateZ(180deg)" : "none",
            }}>
            <AccordionIcon />
          </IconButton>
        </AccordionActions>
      </AccordionSummary>
      <AccordionDetails>
        <Table
          size='small'
          sx={{ "& .MuiTableCell-root": { paddingLeft: 0 } }}>
          <TableHead>
            <TableRow sx={{ "& th": { border: "none", "&:last-of-type": { paddingRight: 0 } } }}>
              <TableCell>Навык</TableCell>
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs?.map((log) => (
              <TableRow
                key={log.id}
                sx={{
                  "&:last-child td": { border: 0 },
                  "& td": { borderLeft: "none", borderRight: "none", "&:last-of-type": { paddingRight: 0 } },
                }}>
                <Tooltip title={dayjs(log.createdAt).format("DD.MM.YYYY HH:mm:ss")}>
                  <TableCell>{log.skillTitle}</TableCell>
                </Tooltip>
                <TableCell>
                  <QuestionsGeneratingLogStatus status={log.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

export default QuestionsGeneratingLogs;
